import { makeAutoObservable, runInAction } from "mobx"

const CSSP_CART_STORAGE_KEY = "cssp_cart"
const OFFER_STALE_AFTER_MS = 2 * 60 * 60 * 1000

export type CsspCartItem = {
  line_key: string
  brand: string
  price: number | null
  count: number
  artikul: string
  description: string
  cprice: number | null
  supplier: string
  time_added: string
  rating: number | null
  stock_on_added: number | null
  delivery_days: number | null
  delivery_days_max: number | null
  warehouse: string
}

type RawCsspCartItem = Partial<CsspCartItem> & {
  dalivery_days?: unknown
}

const readStorage = (): CsspCartItem[] => {
  try {
    const raw = localStorage.getItem(CSSP_CART_STORAGE_KEY)
    if (!raw) return []

    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []

    return parsed.map(normalizeCsspCartItem).filter((item): item is CsspCartItem => item !== null)
  } catch {
    return []
  }
}

const writeStorage = (items: CsspCartItem[]) => {
  localStorage.setItem(CSSP_CART_STORAGE_KEY, JSON.stringify(items.map(serializeCsspCartItem)))
}

const normalizeCsspCartItem = (item: unknown): CsspCartItem | null => {
  if (!item || typeof item !== "object") return null

  const raw = item as RawCsspCartItem
  const brand = asString(raw.brand)
  const artikul = asString(raw.artikul)

  if (!brand || !artikul) return null

  const stockOnAdded = asNumber(raw.stock_on_added)
  const rawCount = Math.max(1, Math.floor(asNumber(raw.count) ?? 1))
  const maxCount = isPositiveInteger(stockOnAdded) ? stockOnAdded : rawCount
  const deliveryDays = asNumber(raw.delivery_days ?? raw.dalivery_days)

  const normalized = {
    brand,
    price: asNumber(raw.price),
    count: clamp(rawCount, 1, Math.max(1, maxCount)),
    artikul,
    description: asString(raw.description) || "Описание не указано",
    cprice: asNumber(raw.cprice),
    supplier: asString(raw.supplier),
    time_added: asString(raw.time_added),
    rating: asNumber(raw.rating),
    stock_on_added: stockOnAdded,
    delivery_days: deliveryDays,
    delivery_days_max: asNumber(raw.delivery_days_max),
    warehouse: asString(raw.warehouse),
  }

  return {
    line_key: asString(raw.line_key) || createCsspCartItemDedupeKey(normalized),
    ...normalized,
  }
}

class CsspCartStore {
  items: CsspCartItem[] = readStorage()

  constructor() {
    makeAutoObservable(this)
  }

  addItem(item: CsspCartItem) {
    runInAction(() => {
      const normalized = normalizeCsspCartItem(item)
      if (!normalized) return

      const nextItems = getStoredOrCurrentItems(this.items)
      const normalizedDedupeKey = createCsspCartItemDedupeKey(normalized)
      const existingIndex = nextItems.findIndex((current) => createCsspCartItemDedupeKey(current) === normalizedDedupeKey)
      if (existingIndex === -1) {
        nextItems.push(normalized)
      } else {
        const current = nextItems[existingIndex]
        nextItems[existingIndex] = isCsspOfferStale(current)
          ? normalized
          : {
              ...current,
              count: clamp(current.count + normalized.count, 1, getCsspCartItemMaxCount(current)),
            }
      }

      this.items = nextItems
      writeStorage(this.items)
    })
  }

  updateQuantity(itemKey: string, nextCount: number) {
    runInAction(() => {
      const nextItems = getStoredOrCurrentItems(this.items)
      const item = nextItems.find((current) => createCsspCartItemKey(current) === itemKey)
      if (!item) return

      if (nextCount <= 0) {
        this.items = nextItems.filter((current) => createCsspCartItemKey(current) !== itemKey)
        writeStorage(this.items)
        return
      }

      item.count = clamp(Math.floor(nextCount), 1, getCsspCartItemMaxCount(item))
      this.items = nextItems
      writeStorage(this.items)
    })
  }

  updateQuantityByDedupeKey(dedupeKey: string, nextCount: number) {
    runInAction(() => {
      const nextItems = getStoredOrCurrentItems(this.items)
      const item = nextItems.find((current) => createCsspCartItemDedupeKey(current) === dedupeKey)
      if (!item) return

      if (nextCount <= 0) {
        this.items = nextItems.filter((current) => createCsspCartItemDedupeKey(current) !== dedupeKey)
        writeStorage(this.items)
        return
      }

      item.count = clamp(Math.floor(nextCount), 1, getCsspCartItemMaxCount(item))
      this.items = nextItems
      writeStorage(this.items)
    })
  }

  removeItem(itemKey: string) {
    runInAction(() => {
      this.items = getStoredOrCurrentItems(this.items).filter((item) => createCsspCartItemKey(item) !== itemKey)
      writeStorage(this.items)
    })
  }

  syncFromStorage() {
    runInAction(() => {
      this.items = readStorage()
    })
  }
}

export const csspCartStore = new CsspCartStore()

export const createCsspCartItemKey = (item: CsspCartItem) =>
  item.line_key || createCsspCartItemDedupeKey(item)

export const createCsspCartItemDedupeKey = (item: Pick<CsspCartItem, "supplier" | "brand" | "artikul" | "cprice" | "delivery_days" | "delivery_days_max" | "warehouse">) =>
  [
    item.supplier,
    item.brand,
    item.artikul,
    item.cprice ?? "",
    item.delivery_days ?? "",
    item.delivery_days_max ?? "",
    item.warehouse,
  ].join("|")

export const getCsspCartItemMaxCount = (item: CsspCartItem) => {
  if (isPositiveInteger(item.stock_on_added)) {
    return item.stock_on_added
  }

  return Math.max(1, item.count)
}

export const isCsspOfferStale = (item: CsspCartItem, now = Date.now()) => {
  const addedAt = Date.parse(item.time_added)
  if (!Number.isFinite(addedAt)) return true

  return now - addedAt > OFFER_STALE_AFTER_MS
}

const asString = (value: unknown) => (typeof value === "string" ? value.trim() : "")

const asNumber = (value: unknown) => {
  if (typeof value === "number" && Number.isFinite(value)) return value
  if (typeof value === "string" && value.trim()) {
    const numeric = Number(value.replace(",", "."))
    return Number.isFinite(numeric) ? numeric : null
  }

  return null
}

const isPositiveInteger = (value: number | null): value is number => value !== null && Number.isInteger(value) && value > 0

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max)

const serializeCsspCartItem = (item: CsspCartItem): CsspCartItem => ({
  line_key: createCsspCartItemKey(item),
  brand: item.brand,
  price: item.price,
  count: item.count,
  artikul: item.artikul,
  description: item.description,
  cprice: item.cprice,
  supplier: item.supplier,
  time_added: item.time_added,
  rating: item.rating,
  stock_on_added: item.stock_on_added,
  delivery_days: item.delivery_days,
  delivery_days_max: item.delivery_days_max,
  warehouse: item.warehouse,
})

const getStoredOrCurrentItems = (currentItems: CsspCartItem[]) => {
  const storedItems = readStorage()
  return storedItems.length > 0 ? storedItems : [...currentItems]
}
